import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Page, SkeletonBodyText, SkeletonDisplayText, BlockStack } from "@shopify/polaris";
import { DevAuthGate } from "./auth/DevAuthGate.js";
import { NavFrame } from "./components/NavFrame.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";

const DashboardRoute = lazy(() => import("./routes/dashboard.js"));
const MessagesRoute = lazy(() => import("./routes/messages.js"));
const SettingsRoute = lazy(() => import("./routes/settings.js"));
const WhatsappConnectRoute = lazy(() => import("./routes/whatsapp-connect.js"));
const BroadcastsListRoute = lazy(() => import("./routes/broadcasts/index.js"));
const NewAiBroadcastRoute = lazy(() => import("./routes/broadcasts/new-ai.js"));
const BroadcastDetailRoute = lazy(() => import("./routes/broadcasts/detail.js"));
const FeedbackRoute = lazy(() => import("./routes/feedback.js"));
const BillingRoute = lazy(() => import("./routes/billing.js"));
const TemplatesRoute = lazy(() => import("./routes/templates.js"));

export function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <DevAuthGate>
        <NavFrame>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardRoute />} />
              <Route path="/messages" element={<MessagesRoute />} />
              <Route path="/settings" element={<SettingsRoute />} />
              <Route path="/whatsapp" element={<WhatsappConnectRoute />} />
              <Route path="/broadcasts" element={<BroadcastsListRoute />} />
              <Route path="/broadcasts/new" element={<Navigate to="/broadcasts/new-ai" replace />} />
              <Route path="/broadcasts/new-ai" element={<NewAiBroadcastRoute />} />
              <Route path="/broadcasts/:id" element={<BroadcastDetailRoute />} />
              <Route path="/feedback" element={<FeedbackRoute />} />
              <Route path="/billing" element={<BillingRoute />} />
              <Route path="/templates" element={<TemplatesRoute />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </NavFrame>
      </DevAuthGate>
    </ErrorBoundary>
  );
}

function RouteFallback(): JSX.Element {
  return (
    <Page title="Loading…">
      <BlockStack gap="400">
        <SkeletonDisplayText size="small" />
        <SkeletonBodyText lines={5} />
      </BlockStack>
    </Page>
  );
}
